import S from 's-js';

/// Draft Signals
/// Draft signals are used as a temporary holding place for written values.  They proxy
/// an underlying signal and, until they are written, report the same value as the underlying
/// signal, including changes.  As soon as they are written to, they start caching, and report their
/// most recently written value, without modifying the underlying signal and irrespective of any changes
/// that occur to the underlying signal.  This allows us to perform functions like validation or saving 
/// values to the server before we modify our main data model.
///
/// Along with their values, draft signals have two additional signals:
///
/// 1. .onWrite(), which is a pure event signal (value `void`) that fires every 
///    time the signal is written.
///
/// 2. .isCaching() which indicates whether the draft is in a caching state.
///
/// To clear a draft signal and stop caching, the written value must be either committed to the
/// underlying signal or reverted (aka discarded).  To accomodate the fact that saving a written 
/// value is often an async process, both committing and reverting are two step processes.
///
/// 1. At the point the value is sampled for saving, take a .checkpoint() of the signal
///
/// 2. Once the save process is finished, either .commit() or .revert() the checkpoint,
///    depending on whether you wish to push the written value into the underlying 
///    signal or not.
///
/// If the draft signal received further writes after the checkpoint was taken, then
/// it will remain in a caching state.  Only when a checkpoint representing the current
/// state is committed or reverted will the draft signal go back to reporting the underlying
/// signal's value.

export interface DraftMethods {
    isCaching() : boolean;
    onWrite() : void;
    checkpoint() : DraftCheckpoint
}

export interface DraftCheckpoint {
    // if this is the latest version to be committed, commits checkpointed value to original signal
    // if this is the current version, clears isCaching()
    commit() : void
    // if this is the current version, clears isCaching()
    revert() : void
}

export interface DraftSignal<T> extends DraftMethods {
    (v? : T) : T;
}

export function DraftSignal<T>(original : (v? : T) => T, eq? : (v1 : T, v2 : T) => boolean) : DraftSignal<T> {
    const
        isCaching = S.value(false),
        cachedValue = S.data<T>(S.sample(original)),
        areEqual = eq || ((v1, v2) => v1 === v2);

    let version = 0,
        committedVersion = version;

    const draft : DraftSignal<T> = <DraftSignal<T>>function (value : T) {
        if (arguments.length === 0) {
            return isCaching() ? cachedValue() : original();
        } else {
            if (!areEqual(value, S.sample(draft))) {
                S.freeze(function () {
                    version++;
                    cachedValue(value);
                    isCaching(true);
                });
            }
            return value;
        }
    };

    draft.isCaching = () => isCaching();
    draft.onWrite = () => { cachedValue(); };

    draft.checkpoint = function () {
        const checkpointValue = S.sample(draft),
            checkpointVersion = version;

        return { commit, revert };

        function commit() {
            S.freeze(() => {
                if (committedVersion < checkpointVersion) {
                    committedVersion = checkpointVersion;
                    original(checkpointValue);
                }
                if (checkpointVersion === version) {
                    isCaching(false);
                }
            });
        };

        function revert() {
            if (checkpointVersion === version) {
                isCaching(false);
            }
        }
    }

    // carry over JSON behavior
    if ((original as any).toJSON) (draft as any).toJSON = (original as any).toJSON;

    return draft;
}

/// DraftObject creates a new, proxying object in which either all the signals or the given list
/// of signals have been replaced with draft signals.  Because we don't want to modify the 
/// original object's signature, the method returns two objects, one the proxy object, the 
/// other holding the draft methods.
export interface DraftObject<T> {
    object: T,
    methods: DraftMethods
}

// TH TODO -- I added an optional eq param to DraftSignal above, mainly thinking about TZDates.
// Might be worth exposing as an option here somehow...
export function DraftObject<T extends object>(original : T, props? : string[]) : DraftObject<T> {
    const object = clone(original),
        signals = [] as DraftSignal<any>[];

    props = props || allObservables(original);

    props.forEach(makeDraftObservable);

    const methods = mergeDraftMethods(...signals);

    return { object, methods };

    function makeDraftObservable(prop : string) {
        var path = prop.split('.'),
            i = 0,
            part = path[i],
            propObject = object as any,
            signal : DraftSignal<any>;

        while (i < path.length - 1) {
            propObject[part] = clone(propObject[part]);
            i++;
            propObject = propObject[part];
            part = path[i];
        }

        if (isObservable(propObject[part])) {
            signal = DraftSignal(propObject[part]);
            propObject[part] = signal;
            signals.push(signal);
        }
    }

    function allObservables(object : any) {
        return Object.keys(object).filter(p => isObservable(object[p]));
    }
}

/// Given two or more draft methods (or observables holding draft methods), construct
/// a new draft methods object that merges the draft methods' signals.  I.e., isCaching()
/// is true if any of the given drafts are caching, onWrite() fires when any of the drafts
/// is written, and checkpoint checkpoints all the drafts, then commits or reverts them as
/// as well.
export function mergeDraftMethods(...drafts : (DraftMethods | (() => DraftMethods | null))[]) : DraftMethods {
    return {
        isCaching: () => unwrap(drafts).some(d => d.isCaching()),
        onWrite: () => { unwrap(drafts).forEach(d => d.onWrite()); },
        checkpoint: () => {
            const checkpoints = unwrap(drafts).map(d => d.checkpoint());
            return {
                commit : () => S.freeze(() => checkpoints.forEach(s => s.commit())),
                revert: () => S.freeze(() => checkpoints.forEach(s => s.revert()))
            }
        }
    };

    function unwrap(drafts : (DraftMethods | (() => DraftMethods | null))[]) : DraftMethods[] {
        const flattened = [] as DraftMethods[];

        for (const _draft of drafts) {
            const draft = isDraftMethods(_draft) ? _draft : _draft();
            if (draft) flattened.push(draft);
        }

        return flattened;
    }
}

/// utils
function isDraftMethods(obj : any) : obj is DraftMethods {
    return obj.checkpoint && obj.checkpoint instanceof Function
        && obj.isCaching && obj.isCaching instanceof Function
        && obj.onWrite && obj.onWrite instanceof Function;
}

function isObservable(obj : any) {
    return obj instanceof Function;
}

function clone<T extends object>(obj : T) : T {
    const clone = Object.create(obj);
    // JSON.stringify looks at Liskov and starts to laugh.
    // By definition, it doesn't pick up inherited properties, so we use a flat copy for serialization, unless the object already has a custom serialization defined
    if (!('toJSON' in clone)) (clone as any).toJSON = flatCopyForJSON;
    return clone;
}

function flatCopyForJSON<T>(this : any) : any {
    var copy = {} as any;
    for (var prop in this) {
        copy[prop] = this[prop];
    }
    return copy;
}


// WEBPACK FOOTER //
// ./lib/draftSignals.ts